import { useFeatureFlags } from '@chronosphereio/core';
import { unifiedMergeView } from '@codemirror/merge';
import type { ReactCodeMirrorRef } from '@uiw/react-codemirror';
import { forwardRef, useMemo } from 'react';
import { useAutocompleteTheme } from '@/code-editor/editor-theme';
import { CodeEditor, CodeEditorProps } from '../code-editor';
import { PromQLExtension } from './codemirror';
import { HTTPPrometheusClient, PrometheusConfig } from './codemirror/client/prometheus';
import { promQLMetricMetadataLinterExtension } from './codemirror/extensions/promql-metric-linter';
import { promQLTooltipExtension } from './codemirror/extensions/promql-tooltip';

export interface PromQLEditorProps extends Omit<CodeEditorProps, 'extensions' | 'tabBehavior'> {
  queryApiEndpoint?: string;
  enableLinter?: boolean;
  timeRange?: PrometheusConfig['timeRange'];

  /**
   * If `previousValue` is provided, user will see a merge/diff view of the previous value and current value.
   */
  previousValue?: string;

  /**
   * Enables autocompletion of built-in variables like $__rate_interval, $__interval, $__range
   * in duration contexts (e.g., inside square brackets like `[5m]` or `[$__rate_interval]`).
   */
  enableBuiltInVariables?: boolean;
}

export const PromQLEditor = forwardRef<ReactCodeMirrorRef, PromQLEditorProps>(function PromQLEditor(
  {
    queryApiEndpoint = '/data/m3',
    enableLinter = true,
    timeRange,
    previousValue,
    enableBuiltInVariables = false,
    ...rest
  }: PromQLEditorProps,
  ref
) {
  const { aiDescriptionsEnabled } = useFeatureFlags();
  const autocompleteTheme = useAutocompleteTheme();

  const extensions = useMemo(() => {
    const client = new HTTPPrometheusClient({
      url: queryApiEndpoint,
      timeRange,
      enableGeneratedDescriptions: aiDescriptionsEnabled,
    });

    const extensions = [
      autocompleteTheme,
      new PromQLExtension()
        .activateLinter(enableLinter)
        .setComplete({
          remote: client,
          enableBuiltInVariables,
        })
        .asExtension(),
    ];

    if (enableLinter) {
      extensions.push(promQLTooltipExtension({ client: client.metadataClient }));
      extensions.push(promQLMetricMetadataLinterExtension({ client: client.metadataClient }));
    }
    if (previousValue) {
      extensions.push(
        unifiedMergeView({
          allowInlineDiffs: false,
          mergeControls: false,
          original: previousValue,
        })
      );
    }

    return extensions;
  }, [
    queryApiEndpoint,
    timeRange,
    aiDescriptionsEnabled,
    autocompleteTheme,
    enableLinter,
    previousValue,
    enableBuiltInVariables,
  ]);

  return (
    <CodeEditor placeholder="Enter a PromQL query" {...rest} extensions={extensions} ref={ref} tabBehavior="focus" />
  );
});
